import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CellInput } from "@/components/ui/cell-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Building2, MapPin, Ruler, Copy, TrendingUp, Info } from "lucide-react";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useCurrency } from "@/lib/currency";

const STEPS = [
  { id: 0, label: "Project Info", icon: Building2 },
  { id: 1, label: "Location & Type", icon: MapPin },
  { id: 2, label: "Size Metrics", icon: Ruler },
  { id: 3, label: "Review", icon: Check },
];

export default function ProjectWizard() {
  const navigate = useNavigate();
  const cur = useCurrency();
  const [step, setStep] = useState(0);
  const [settings, setSettings] = useState(null);
  const [stages, setStages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    name: "", client_name: "", location: "", sector: "", project_type: "", procurement_route: "",
    stage_framework: "RIBA", disciplines: ["Mechanical", "Electrical", "Public Health"],
    construction_value: 0, reference_number: "", notes: "", modelling_method: "BIM",
    size_metrics: { floor_area: 0, number_of_floors: 0, residential_units: 0, plant_rooms: 0, risers: 0, technical_systems: [] },
  });

  const [templates, setTemplates] = useState([]);
  const [availableTechSystems, setAvailableTechSystems] = useState([]);
  const [quickPrice, setQuickPrice] = useState(null);
  const [quickPriceLoading, setQuickPriceLoading] = useState(false);

  useEffect(() => {
    Promise.all([API.get("/admin/settings"), API.get("/admin/stages"), API.get("/project-templates"), API.get("/admin/technical-systems")])
      .then(([s, st, t, ts]) => { setSettings(s.data); setStages(st.data); setTemplates(t.data); setAvailableTechSystems((ts.data || []).filter(sys => sys.active)); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    const fetchQuickPrice = async () => {
      // Require floor area — sector alone is too broad for meaningful guidance
      if (!form.size_metrics.floor_area || form.size_metrics.floor_area <= 0) { setQuickPrice(null); return; }
      setQuickPriceLoading(true);
      try {
        const res = await API.post("/projects/quick-price", { sector: form.sector || "", floor_area: form.size_metrics.floor_area || 0, procurement_route: form.procurement_route || "", complexity_level: 1.0 });
        setQuickPrice(res.data);
      } catch { setQuickPrice(null); }
      finally { setQuickPriceLoading(false); }
    };
    const timeout = setTimeout(fetchQuickPrice, 400);
    return () => clearTimeout(timeout);
  }, [form.sector, form.size_metrics.floor_area, form.procurement_route]);

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const updateSize = (key, value) => setForm((prev) => ({ ...prev, size_metrics: { ...prev.size_metrics, [key]: parseFloat(value) || 0 } }));
  const toggleDiscipline = (disc) => setForm((prev) => ({ ...prev, disciplines: prev.disciplines.includes(disc) ? prev.disciplines.filter((x) => x !== disc) : [...prev.disciplines, disc] }));
  const canProceed = () => { if (step === 0) return form.name && form.client_name; return true; };

  const applyTemplate = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (tpl) {
      setForm((prev) => ({ ...prev, sector: tpl.sector || prev.sector, project_type: tpl.project_type || prev.project_type, disciplines: tpl.disciplines || prev.disciplines, stage_framework: tpl.stage_framework || prev.stage_framework, template_id: templateId }));
      toast.success(`Applied "${tpl.name}" template`);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.client_name) { toast.error("Project name and client name are required"); return; }
    setSubmitting(true);
    try {
      const res = await API.post("/projects", form);
      toast.success("Project created");
      navigate(`/projects/${res.data.id}`);
    } catch (e) { toast.error("Failed to create project"); }
    finally { setSubmitting(false); }
  };

  const inputClass = "bg-slate-950 border-slate-800 text-white placeholder:text-slate-600 focus:ring-1 focus:ring-blue-500 focus:border-blue-500";
  const labelClass = "text-xs font-medium text-slate-400 mb-1.5 block";

  return (
    <div data-testid="project-wizard" className="max-w-5xl mx-auto space-y-8">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">New Project</h1>
        <p className="text-sm text-slate-500 mt-1">Set up a new MEP pricing project</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 flex-1">
            <button
              onClick={() => i <= step && setStep(i)}
              data-testid={`wizard-step-${i}`}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all w-full ${
                i === step ? "bg-blue-600 text-white" :
                i < step ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                "bg-slate-800 text-slate-500"
              }`}
            >
              {i < step ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
              <span className="hidden md:inline">{s.label}</span>
            </button>
            {i < STEPS.length - 1 && <div className="w-4 h-px bg-slate-800 flex-shrink-0" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
          <CardContent className="p-6">
            {step === 0 && (
              <div className="space-y-5" data-testid="step-project-info">
                {templates.length > 0 && (
                  <div>
                    <Label className={labelClass}>Start from Template</Label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {templates.map((t) => (
                        <Button key={t.id} variant="outline" size="sm" onClick={() => applyTemplate(t.id)} className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white text-xs" data-testid={`template-${t.id}`}>
                          <Copy className="w-3 h-3 mr-1" />{t.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <Label className={labelClass}>Project Name *</Label>
                  <Input data-testid="input-project-name" value={form.name} onChange={(e) => updateForm("name", e.target.value)} className={inputClass} placeholder="e.g. 100 Bishopsgate Office Tower" />
                </div>
                <div>
                  <Label className={labelClass}>Client Name *</Label>
                  <Input data-testid="input-client-name" value={form.client_name} onChange={(e) => updateForm("client_name", e.target.value)} className={inputClass} placeholder="e.g. Brookfield Properties" />
                </div>
                <div>
                  <Label className={labelClass}>Internal Reference Number</Label>
                  <Input data-testid="input-reference" value={form.reference_number} onChange={(e) => updateForm("reference_number", e.target.value)} className={inputClass} placeholder="e.g. PRJ-2026-001" />
                </div>
                <div>
                  <Label className={labelClass}>Disciplines</Label>
                  <div className="flex gap-4 mt-2">
                    {["Mechanical", "Electrical", "Public Health"].map((d) => (
                      <label key={d} className="flex items-center gap-2 text-sm text-slate-300" data-testid={`discipline-${d.toLowerCase()}`}>
                        <Checkbox checked={form.disciplines.includes(d)} onCheckedChange={() => toggleDiscipline(d)} />
                        {d}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className={labelClass}>Stage Framework</Label>
                  <Select value={form.stage_framework} onValueChange={(v) => updateForm("stage_framework", v)}>
                    <SelectTrigger className={inputClass} data-testid="select-framework"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {stages.map((s) => <SelectItem key={s.framework} value={s.framework}>{s.framework}</SelectItem>)}
                      {stages.length === 0 && <SelectItem value="RIBA">RIBA</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelClass}>Modelling Method</Label>
                  <div className="flex gap-4 mt-2">
                    {[{ value: "BIM", label: "BIM", desc: "Building Information Modelling" }, { value: "2D CAD", label: "2D CAD", desc: "Traditional 2D drafting" }].map((m) => (
                      <button
                        key={m.value}
                        onClick={() => updateForm("modelling_method", m.value)}
                        data-testid={`modelling-${m.value.toLowerCase().replace(/\s/g, "-")}`}
                        className={`flex-1 p-3 rounded-lg border-2 text-left transition-all ${
                          form.modelling_method === m.value
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-slate-800 hover:border-slate-700"
                        }`}
                      >
                        <p className={`text-sm font-semibold ${form.modelling_method === m.value ? "text-blue-400" : "text-slate-300"}`}>{m.label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                      </button>
                    ))}
                  </div>
                  {form.modelling_method === "2D CAD" && (
                    <p className="text-xs text-amber-400 mt-2">BIM-related effort drivers will not be applied to this project.</p>
                  )}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="step-location-type">
                <div><Label className={labelClass}>Location</Label><Input data-testid="input-location" value={form.location} onChange={(e) => updateForm("location", e.target.value)} className={inputClass} placeholder="e.g. London, EC2" /></div>
                <div>
                  <Label className={labelClass}>Sector</Label>
                  <Select value={form.sector} onValueChange={(v) => updateForm("sector", v)}>
                    <SelectTrigger className={inputClass} data-testid="select-sector"><SelectValue placeholder="Select sector" /></SelectTrigger>
                    <SelectContent>{(settings?.sectors || ["Commercial", "Residential", "Healthcare", "Education", "Industrial", "Retail", "Mixed Use", "Data Centre", "Hospitality"]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={labelClass}>Project Type</Label>
                  <Select value={form.project_type} onValueChange={(v) => updateForm("project_type", v)}>
                    <SelectTrigger className={inputClass} data-testid="select-project-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                    <SelectContent>{(settings?.project_types || ["New Build", "Refurbishment", "Fit Out", "Extension", "Masterplan"]).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">Procurement Route <InfoTooltip text="The procurement route affects the multiplier applied to fees. Traditional (1.0x) is standard. Design & Build (0.9x) typically reduces scope. Construction Management (1.1x) increases coordination effort." /></Label>
                  <Select value={form.procurement_route} onValueChange={(v) => updateForm("procurement_route", v)}>
                    <SelectTrigger className={inputClass} data-testid="select-procurement"><SelectValue placeholder="Select route" /></SelectTrigger>
                    <SelectContent>{["Traditional", "Design and Build", "Two-stage tender", "Management Contract", "Construction Management"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1.5">Estimated Construction Value <InfoTooltip text="The estimated total construction cost. This is used to benchmark your fee as a percentage of construction value. Typical MEP fees range from 2-8% of construction value." /></Label>
                  <CellInput data-testid="input-construction-value" type="number" value={form.construction_value || ""} onChange={(e) => updateForm("construction_value", parseFloat(e.target.value) || 0)} className={`${inputClass} font-mono`} placeholder="e.g. 5000000" />
                </div>
                <div><Label className={labelClass}>Notes</Label><Textarea data-testid="input-notes" value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} className={`${inputClass} min-h-[80px]`} placeholder="Additional project notes..." rows={3} /></div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5" data-testid="step-size-metrics">
                {[
                  { key: "floor_area", label: "Floor Area (sqm)", placeholder: "e.g. 5000" },
                  { key: "number_of_floors", label: "Number of Floors", placeholder: "e.g. 10" },
                  { key: "residential_units", label: "Residential Units", placeholder: "e.g. 0" },
                  { key: "plant_rooms", label: "Plant Rooms", placeholder: "e.g. 2" },
                  { key: "risers", label: "Risers", placeholder: "e.g. 4" },
                ].map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <Label className={labelClass}>{label}</Label>
                    <CellInput data-testid={`input-${key}`} type="number" value={form.size_metrics[key] || ""} onChange={(e) => updateSize(key, e.target.value)} className={`${inputClass} font-mono`} placeholder={placeholder} />
                  </div>
                ))}

                {/* Technical Systems Multi-Select */}
                <div>
                  <Label className={labelClass}>Technical Systems</Label>
                  <p className="text-xs text-slate-500 mb-3">Select the MEP systems relevant to this project. The count feeds into the size scaling multiplier.</p>
                  {["Mechanical", "Electrical", "Public Health"].map(group => {
                    const groupSystems = availableTechSystems.filter(s => s.group === group);
                    if (groupSystems.length === 0) return null;
                    return (
                      <div key={group} className="mb-3">
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1.5">{group}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {groupSystems.map(sys => {
                            const selected = (form.size_metrics?.technical_systems || []).includes(sys.name);
                            return (
                              <button key={sys.id} type="button" data-testid={`wizard-tech-sys-${sys.name.replace(/[\s\/]/g, "-").toLowerCase()}`} onClick={() => setForm(prev => {
                                const current = prev.size_metrics?.technical_systems || [];
                                const next = selected ? current.filter(n => n !== sys.name) : [...current, sys.name];
                                return { ...prev, size_metrics: { ...prev.size_metrics, technical_systems: next } };
                              })} className={`text-[11px] px-2.5 py-1.5 rounded-md border font-medium transition-all ${selected ? "bg-blue-600/20 border-blue-500 text-blue-300 shadow-sm shadow-blue-500/10" : "bg-slate-800 border-slate-700 text-slate-500 hover:text-slate-300 hover:border-slate-600"}`}>
                                {selected && <span className="mr-1">✓</span>}{sys.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {(form.size_metrics?.technical_systems || []).length > 0 && (
                    <div className="mt-2 text-xs font-mono text-blue-400" data-testid="wizard-tech-systems-count">
                      {(form.size_metrics.technical_systems || []).length} system{(form.size_metrics.technical_systems || []).length !== 1 ? "s" : ""} selected
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="step-review">
                <h3 className="font-heading text-lg font-semibold text-slate-100">Review & Create</h3>
                <div className="divide-y divide-slate-800">
                  {[
                    ["Project Name", form.name],
                    ["Client", form.client_name],
                    ["Reference", form.reference_number || "—"],
                    ["Location", form.location || "—"],
                    ["Sector", form.sector || "—"],
                    ["Type", form.project_type || "—"],
                    ["Procurement", form.procurement_route || "—"],
                    ["Framework", form.stage_framework],
                    ["Modelling", form.modelling_method],
                    ["Disciplines", form.disciplines.join(", ")],
                    ["Construction Value", form.construction_value ? `${cur.symbol}${form.construction_value.toLocaleString()}` : "—"],
                    ["Floor Area", form.size_metrics.floor_area ? `${form.size_metrics.floor_area} sqm` : "—"],
                    ["Floors", form.size_metrics.number_of_floors || "—"],
                    ["Technical Systems", (form.size_metrics.technical_systems || []).length > 0 ? `${form.size_metrics.technical_systems.length} selected` : "—"],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between py-2.5">
                      <span className="text-sm text-slate-500">{label}</span>
                      <span className="text-sm font-medium text-slate-200">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-between mt-8 pt-4 border-t border-slate-800">
              <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="wizard-prev" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              {step < 3 ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canProceed()} data-testid="wizard-next" className="bg-blue-600 hover:bg-blue-500 text-white">
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={submitting} data-testid="wizard-create" className="bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-lg shadow-blue-900/20">
                  {submitting ? "Creating..." : "Create Project"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Side Summary + Quick Price */}
        <div className="space-y-4">
          <Card className="bg-slate-900 border-slate-800 h-fit sticky top-20">
            <CardHeader className="border-b border-slate-800 py-3 px-4">
              <CardTitle className="font-heading text-sm font-semibold text-slate-100">Project Summary</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div><p className="text-xs text-slate-500 uppercase tracking-wider">Project</p><p className="text-sm font-medium text-slate-200 mt-0.5">{form.name || "—"}</p></div>
              <div><p className="text-xs text-slate-500 uppercase tracking-wider">Client</p><p className="text-sm font-medium text-slate-200 mt-0.5">{form.client_name || "—"}</p></div>
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Disciplines</p>
                <div className="flex gap-1 mt-1">{form.disciplines.map((d) => <span key={d} className="text-xs px-2 py-0.5 bg-slate-800 text-slate-400 rounded">{d}</span>)}</div>
              </div>
              {form.sector && <div><p className="text-xs text-slate-500 uppercase tracking-wider">Sector</p><p className="text-sm text-slate-300 mt-0.5">{form.sector}</p></div>}
              {form.construction_value > 0 && <div><p className="text-xs text-slate-500 uppercase tracking-wider">Construction Value</p><p className="text-sm font-mono font-medium text-slate-200 mt-0.5">{cur.symbol}{form.construction_value.toLocaleString()}</p></div>}
            </CardContent>
          </Card>

          {(quickPrice?.available || quickPriceLoading) && (
            <Card className="bg-slate-900 border-blue-500/20" data-testid="quick-price-panel">
              <CardHeader className="border-b border-blue-500/10 py-3 px-4">
                <CardTitle className="font-heading text-sm font-semibold text-slate-100 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-500" />Quick Price Guidance
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                {quickPriceLoading ? (
                  <div className="space-y-2 animate-pulse"><div className="h-4 bg-slate-800 rounded w-3/4" /><div className="h-8 bg-slate-800 rounded w-full" /></div>
                ) : quickPrice?.available ? (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-500">{quickPrice.message}</p>
                    <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700">
                      <div className="text-center">
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Indicative Fee Range</p>
                        <p className="text-2xl font-heading font-bold text-slate-100 mt-1">{cur.symbol}{Number(quickPrice.suggestion.typical_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</p>
                        <p className="text-xs text-slate-500 mt-1">{cur.symbol}{Number(quickPrice.suggestion.low_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}<span className="mx-1.5 text-slate-600">—</span>{cur.symbol}{Number(quickPrice.suggestion.high_fee).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-700 grid grid-cols-2 gap-2">
                        <div><p className="text-[10px] text-slate-500 uppercase">Avg Hours</p><p className="text-xs font-mono font-semibold text-slate-200">{Number(quickPrice.suggestion.avg_hours).toLocaleString()}</p></div>
                        <div><p className="text-[10px] text-slate-500 uppercase">Avg Margin</p><p className="text-xs font-mono font-semibold text-slate-200">{quickPrice.suggestion.avg_margin_pct}%</p></div>
                        <div className="col-span-2"><p className="text-[10px] text-slate-500 uppercase">Projects Compared</p><p className="text-xs font-mono font-semibold text-slate-200">{quickPrice.comparable_count}</p></div>
                      </div>
                    </div>
                    <div className="flex items-start gap-1.5">
                      <Info className="w-3 h-3 text-slate-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[10px] text-slate-600 leading-relaxed">Indicative only. Fee will be calculated through the effort-based pricing engine after scope definition.</p>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
          {quickPrice && !quickPrice.available && (form.sector || form.size_metrics.floor_area > 0) && (
            <Card className="bg-slate-900 border-slate-800" data-testid="quick-price-empty">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
                  <div><p className="text-xs font-semibold text-slate-500">Quick Price</p><p className="text-xs text-slate-600 mt-0.5">{quickPrice.message}</p></div>
                </div>
              </CardContent>
            </Card>
          )}
          {!quickPrice && !quickPriceLoading && form.sector && !form.size_metrics.floor_area && (
            <Card className="bg-slate-900 border-slate-800" data-testid="quick-price-hint">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <TrendingUp className="w-4 h-4 text-slate-600 flex-shrink-0 mt-0.5" />
                  <div><p className="text-xs font-semibold text-slate-500">Quick Price</p><p className="text-xs text-slate-600 mt-0.5">Enter a floor area in Project Size to unlock indicative fee guidance.</p></div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
